import { css } from "@emotion/css";
import { ActionButton, ActionButtonType, Callout } from "@octopusdeploy/design-system-components";
import { space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import { useQuery } from "@octopusdeploy/octopus-react-client";
import type { ProcessTemplateResource, EnvironmentResource, GitRefResource, PackageReference, ProcessTemplateParameterResource, ScopeValues, VariableSetResource, PlatformHubAccountSummary, PropertyValueResource, } from "@octopusdeploy/octopus-server-client";
import { noOp } from "@octopusdeploy/utilities";
import { flatten } from "lodash";
import { useMemo, useState } from "react";
import * as React from "react";
import type { ParameterValueRow, ProcessTemplateValueKey } from "~/areas/blueprints/ProcessTemplateParametersTab";
import { ProcessTemplateParameterScopeCell } from "~/areas/blueprints/ProcessTemplateParametersTab";
import { useScopeWidth } from "~/areas/blueprints/useScopeWidth";
import { useProcessContext } from "~/areas/projects/components/Process/Contexts/ProcessContext";
import { useProcessErrorSelectors } from "~/areas/projects/components/Process/Contexts/ProcessErrors/ProcessErrorsContext";
import { useFeedsFromContext, useRefreshFeedsFromContext } from "~/areas/projects/components/Process/Contexts/ProcessFeedsContextProvider";
import type { FailedPermissionCheck } from "~/areas/projects/components/Process/ProcessStepsLayoutTypes";
import type { StoredAction } from "~/areas/projects/components/Process/types";
import type { LoadedLibraryVariableSets } from "~/areas/projects/components/Variables/AllVariables/AllVariables";
import ReadonlyInlineVariableValue from "~/areas/projects/components/Variables/TenantVariables/ReadonlyInlineVariableValue";
import { TemplateHelpText } from "~/areas/projects/components/Variables/TenantVariables/TemplateHelpText";
import { getVariableTypeFromDisplaySettings } from "~/areas/projects/components/Variables/TenantVariables/getVariableTypeFromDisplaySettings";
import { useProjectContext } from "~/areas/projects/context";
import { isAccountType } from "~/areas/variables/isAccountType";
import { repository } from "~/clientInstance";
import type { ProjectSourceItems } from "~/components/ActionTemplateParameterInput/ActionTemplateParameterInput";
import ActionTemplateParameterInput from "~/components/ActionTemplateParameterInput/ActionTemplateParameterInput";
import { createSummaryFor } from "~/components/ActionTemplateParameterInput/ActionTemplateParameterInputExpandableFormElement";
import { getHelpText, getLabel } from "~/components/ActionTemplateParameterInput/ActionTemplateParameterInputRenderer";
import { type DoBusyTask } from "~/components/DataBaseComponent/index";
import { GroupedDataTable } from "~/components/GroupedDataTable/GroupedDataTable";
import { ExpandableFormSection, FormSection, FormSectionHeading, Summary } from "~/components/form/index";
import selectOptionsToItems from "~/primitiveComponents/form/Select/Options";
interface BlueprintActionDetailsProps {
    stepId: string;
    action: StoredAction;
    doBusyTask: DoBusyTask;
    onParameterValueChanged: (parameterName: string, value: PropertyValueResource | undefined) => void;
    blueprint: ProcessTemplateResource;
    gitRefResource: GitRefResource | undefined;
    processVariableSet: VariableSetResource | FailedPermissionCheck;
    libraryVariableSets: LoadedLibraryVariableSets[] | FailedPermissionCheck;
    environments: EnvironmentResource[];
}
export function BlueprintActionDetails({ stepId, action, doBusyTask, onParameterValueChanged, blueprint, gitRefResource, processVariableSet, libraryVariableSets, environments }: BlueprintActionDetailsProps) {
    const projectContext = useProjectContext();
    const processContext = useProcessContext();
    const actionNames: string[] = flatten(Object.values(processContext.state.model.actions.byId).map((a) => a.Name));
    const feeds = useFeedsFromContext();
    const refreshFeeds = useRefreshFeedsFromContext();
    const platformHubAccountIds = useMemo(() => blueprint.Parameters.filter((p) => isAccountType(getVariableTypeFromDisplaySettings(p.DisplaySettings["Octopus.ControlType"]))).flatMap((p) => p.Values.map((v) => v.Value)), [blueprint.Parameters]);
    const { result: platformHubAccountNames } = useQuery(() => repository.PlatformHub.getPlatformHubAccountNames(platformHubAccountIds), [platformHubAccountIds], "Get platform hub account names");
    const packageReferences: PackageReference[] = blueprint.Parameters.filter((p) => p.DisplaySettings["Octopus.ControlType"] === "Package").map((p) => ({
        Name: p.Name,
        PackageId: "",
        FeedId: "",
        AcquisitionLocation: "",
        Properties: {
            PackageParameterName: p.Name,
            SelectionMode: "deferred",
        },
        Id: "",
    }));
    if (!platformHubAccountNames) {
        return null;
    }
    const sourceItems: ProjectSourceItems = {
        stepNames: actionNames,
        projectId: projectContext.state.model.Id,
        processIdentifier: processContext.state.processIdentifier,
        processVariableSet,
        libraryVariableSets,
        refreshVariables: undefined, // Can't refresh variables in a process template action
        gitRef: gitRefResource,
        packages: {
            items: packageReferences,
            feeds: feeds,
            onRequestRefresh: async () => {
                await doBusyTask(async () => {
                    await refreshFeeds();
                });
            },
            setPackages: (packages: PackageReference[]) => {
                // TODO: Untested
                processContext.actions.setActionMetaProperties(action.Id, (prev) => ({ ...prev, Packages: packages }));
            },
        },
    };
    const parameterNamesInAction = Object.keys(action.Properties).filter((p) => !p.startsWith("Octopus"));
    const parameterNamesInBlueprint = new Set(blueprint.Parameters.map((p) => p.Name));
    const parametersRemovedFromBlueprint = parameterNamesInAction.filter((p) => !parameterNamesInBlueprint.has(p));
    const clearValues = () => {
        processContext.actions.removeActionProperties(action.Id, parametersRemovedFromBlueprint);
    };
    return (<>
            <FormSectionHeading title="Process Template Details"/>
            <ReadonlyBlueprintDetails blueprint={blueprint}/>
            {blueprint.Parameters.length > 0 ? (<>
                    <FormSectionHeading title="Parameters"/>
                    {parametersRemovedFromBlueprint.length > 0 ? (<Callout type={"information"} title={"Clear old parameter values"}>
                            <div className={styles.calloutContainer}>
                                <div>One or more parameters have been removed from the current version of the blueprint. We recommend clearing these values to avoid any unexpected behavior.</div>
                                <div>
                                    <ActionButton type={ActionButtonType.Primary} label={"Clear values"} onClick={clearValues}/>
                                </div>
                            </div>
                        </Callout>) : null}
                    <ProcessTemplateParametersReadonlyTable parameters={blueprint.Parameters} availableScopeValues={{ ...defaultScopeValues, Environments: environments }} platformHubAccounts={platformHubAccountNames}/>
                    <FormSectionHeading title="Overrides"/>
                    <Callout type={"information"} title={"Provide an override for template parameters"}>
                        <div>Overrides will be used when there is no scoped value provided by the process template for a given environment.</div>
                    </Callout>
                    {blueprint.Parameters.map((parameter) => (<BlueprintParameter key={parameter.Name} stepId={stepId} parameter={parameter} sourceItems={sourceItems} action={action} onParameterValueChanged={onParameterValueChanged} doBusyTask={doBusyTask}/>))}
                    {parametersRemovedFromBlueprint.map((parameter) => (<FormSection key={parameter} title={<span className={styles.removedParameter}>{parameter}</span>} includeBorder={true} help={<span className={styles.removedParameter}>{action.Properties[parameter]}</span>}></FormSection>))}
                </>) : null}
        </>);
}
interface ProcessTemplateParametersReadonlyTableProps {
    parameters: ProcessTemplateParameterResource[];
    availableScopeValues: ScopeValues;
    platformHubAccounts: PlatformHubAccountSummary[];
}
function ProcessTemplateParametersReadonlyTable({ parameters, availableScopeValues, platformHubAccounts }: ProcessTemplateParametersReadonlyTableProps) {
    const { scopeHeaderRef, scopeWidth } = useScopeWidth();
    const [focusedValueKey, setFocusedValueKey] = useState<ProcessTemplateValueKey | null>(null);
    const parameterValues: ParameterValueRow[] = parameters.flatMap((p, parameterIndex): ParameterValueRow[] => {
        if (p.Values.length === 0) {
            return [{ Parameter: p, Value: null, ParameterIndex: parameterIndex, ValueIndex: 0 }];
        }
        return p.Values.map<ParameterValueRow>((v, valueIndex) => ({ Parameter: p, Value: v, ParameterIndex: parameterIndex, ValueIndex: valueIndex }));
    });
    return (<div>
            <GroupedDataTable data={parameterValues} columns={[
            {
                title: "Name",
                render: (data) => (data.ValueIndex === 0 ? <ReadonlyParameterNameCell parameter={data.Parameter}/> : null),
                width: "25%",
                isChild: (data) => data.ValueIndex !== 0,
                padding: space[12],
            },
            {
                title: "Value",
                render: (data) => (<div className={styles.valueContainer}>
                                {data.Value ? (<ReadonlyInlineVariableValue value={data.Value?.Value} variableType={getVariableTypeFromDisplaySettings(data.Parameter.DisplaySettings["Octopus.ControlType"])} defaultValue={undefined} accounts={platformHubAccounts.reduce((acc, a) => ({ ...acc, [a.Id]: a.Name }), {})} certificates={{}} workerPools={{}} hasEditPermissions={false} sourceItems={undefined}/>) : (<div className={styles.noValue}>No values provided. Specify an override below.</div>)}
                            </div>),
                width: "35%",
                padding: "0",
            },
            {
                title: "Scope",
                render: (data) => data.Value ? (<div className={styles.scopeContainer}>
                                    <ProcessTemplateParameterScopeCell parameter={data.Parameter} value={data.Value} onChange={noOp} index={data.ValueIndex} isEditing={false} focusedValueKey={focusedValueKey} setFocusedValueKey={setFocusedValueKey} scopeValues={availableScopeValues} height={48} width={scopeWidth} isConsumer={true}/>
                                </div>) : null,
                width: "40%",
                padding: "0",
                ref: scopeHeaderRef,
            },
        ]} getRowKey={(data) => `${data.Parameter.Name}-${data.ValueIndex}`}/>
        </div>);
}
function ReadonlyParameterNameCell({ parameter }: {
    parameter: ProcessTemplateParameterResource;
}) {
    return (<div className={styles.parameterNameCell}>
            <div className={styles.parameterLabelOrName}>{parameter.Label || parameter.Name}</div>
            <TemplateHelpText text={parameter.HelpText}/>
        </div>);
}
const defaultScopeValues: ScopeValues = {
    Actions: [],
    Channels: [],
    Environments: [],
    Machines: [],
    Roles: [],
    TenantTags: [],
    Processes: [],
};
interface ReadonlyBlueprintDetailsProps {
    blueprint: ProcessTemplateResource;
}
function ReadonlyBlueprintDetails({ blueprint }: ReadonlyBlueprintDetailsProps) {
    return (<>
            <FormSection title={"Description"} includeBorder={true} help={blueprint.Description}></FormSection>
        </>);
}
interface BlueprintParameterProps {
    parameter: ProcessTemplateParameterResource;
    stepId: string;
    sourceItems: ProjectSourceItems;
    action: StoredAction;
    onParameterValueChanged: (parameterName: string, value: PropertyValueResource | undefined) => void;
    doBusyTask: DoBusyTask;
}
function BlueprintParameter({ stepId, parameter, sourceItems, action, onParameterValueChanged, doBusyTask }: BlueprintParameterProps) {
    const projectContext = useProjectContext();
    const label = getLabel(parameter);
    const type = parameter.DisplaySettings["Octopus.ControlType"];
    const options = selectOptionsToItems(parameter.DisplaySettings["Octopus.SelectOptions"]);
    const packageParameterIsUsed = !!sourceItems.packages.items.find((p) => p.Properties["PackageParameterName"] === parameter.Name);
    const value = action.Properties[parameter.Name];
    const hasValue = parameter.Values.length > 0;
    const optionalText = hasValue ? " (optional)" : "";
    const processErrorSelectors = useProcessErrorSelectors();
    const getStepFieldError = (value: string) => processErrorSelectors.getStepFieldError(stepId, value);
    const summary = value === undefined || value === null ? Summary.placeholder("Provide " + label + optionalText) : type ? createSummaryFor(label, type, options, sourceItems.packages.feeds, packageParameterIsUsed)(value) : null;
    const helpText = (<div className={styles.helpText}>
            {getHelpText(parameter, undefined, packageParameterIsUsed)} <span className={styles.optionalText}>{optionalText}</span>
        </div>);
    return type ? (<ExpandableFormSection errorKey={parameter.Name} title={label} isExpandedByDefault={!value} help={helpText} summary={summary}>
            <ActionTemplateParameterInput parameter={parameter} sourceItems={sourceItems} doBusyTask={doBusyTask} value={value} onChange={(value) => onParameterValueChanged(parameter.Name, value)} actionType={action.ActionType} error={getStepFieldError(parameter.Name)}/>
        </ExpandableFormSection>) : null;
}
const styles = {
    calloutContainer: css({
        display: "flex",
        flexDirection: "column",
        gap: space[8],
    }),
    removedParameter: css({
        color: themeTokens.color.text.disabled,
    }),
    parameterNameCell: css({
        display: "flex",
        alignItems: "center",
        gap: space[8],
    }),
    parameterLabelOrName: css({
        font: text.body.bold.medium,
        wordBreak: "break-word",
    }),
    valueContainer: css({
        paddingLeft: space[12],
        paddingRight: space[12],
    }),
    noValue: css({
        color: themeTokens.color.text.secondary,
    }),
    optionalText: css({
        color: themeTokens.color.text.tertiary,
        marginLeft: space[4],
    }),
    helpText: css({
        display: "flex",
        flexDirection: "row",
        alignItems: "center",
    }),
    scopeContainer: css({
        height: "3rem",
        cursor: "default",
    }),
};
